import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import type { RootState } from "src/redux/store";
import { LabVendor, LabVendorLocationConfig } from "src/schemas/settings/labs/LabVendor";
import { includesSuperAdminRoleGroup } from "src/utils/roles";
import { match } from "ts-pattern";

export type LabVendorConfigDrawerState = {
    show: boolean;
    onCloseDrawer?: () => void;
    labVendor?: LabVendor;
    saveStatus: "idle" | "pending" | "success" | "failed";
};

export const openLabsVendorConfigDrawer = createAction<{
    vendor: LabVendor;
    onCloseDrawer?: () => void;
}>("labs/settings/vendor/open");

export const closeLabsVendorConfigDrawer = createAction("labs/settings/vendor/close");

export const addLocationConfigurationToVendor = createAction<LabVendorLocationConfig>(
    "labs/settings/vendor/add/location-config",
);
export const setLocationConfigurationForVendor = createAction<LabVendorLocationConfig>(
    "labs/settings/vendor/set/location-config",
);
export const removeLocationConfigurationFromVendor = createAction<number>(
    "labs/settings/vendor/remove/location-config",
);

export type SaveLabVendorError = {
    message: string;
};

export const saveLabsVendorConfigStaff = createAsyncThunk<
    LabVendor,
    LabVendor,
    { rejectValue: SaveLabVendorError }
>("labs/settings/vendor/save/staff", async (vendor, thunk) => {
    const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/settings/vendors`;
    const payload = {
        id: vendor.id,
        vendorType: vendor.vendorType,
        username: vendor.username !== "" ? vendor.username : undefined,
        password: vendor.password !== "" ? vendor.password : undefined,
        externalId: vendor.externalId,
        labName: vendor.labName,
        labDirectorStaffId: vendor.labDirectorStaffLite?.id,
        clia: vendor.clia,
        stateLicenseNumber: vendor.stateLicenseNumber,
        address: vendor.address,
        locationConfigs: vendor.locationConfigs?.map((cfg) => ({
            id: cfg.id,
            labVendorId: vendor.id,
            locationId: cfg.locationId,
            externalId: cfg.externalId,
            labName: cfg.labName,
            labDirectorStaffId: cfg.labDirectorStaffLite?.id,
            clia: cfg.clia,
            stateLicenseNumber: cfg.stateLicenseNumber,
            address: cfg.address,
        })),
    };
    try {
        return (await axios.post<LabVendor>(route, payload, { withCredentials: true })).data;
    } catch (error) {
        // TODO: need to consume the error and transform it into something consumable
        return thunk.rejectWithValue({ message: "failed to save vendor configuration" });
    }
});

export const saveLabsVendorConfigAdmin = createAsyncThunk<
    LabVendor,
    LabVendor,
    { rejectValue: SaveLabVendorError }
>(
    "labs/settings/vendor/save/admin",
    async (vendor, thunk) => {
        const state = thunk.getState() as RootState;
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/admin/labs/settings/vendors?accountPublicUuid=${state.account?.account?.publicUuid}`;
        const payload = {
            id: vendor.id,
            vendorType: vendor.vendorType,
            username: vendor.username !== "" ? vendor.username : undefined,
            password: vendor.password !== "" ? vendor.password : undefined,
            externalId: vendor.externalId,
            labName: vendor.labName,
            labDirectorStaffId: vendor.labDirectorStaffLite?.id,
            clia: vendor.clia,
            stateLicenseNumber: vendor.stateLicenseNumber,
            address: vendor.address,
            locationConfigs: vendor.locationConfigs?.map((cfg) => ({
                id: cfg.id,
                labVendorId: vendor.id,
                locationId: cfg.locationId,
                externalId: cfg.externalId,
                labName: cfg.labName,
                labDirectorStaffId: cfg.labDirectorStaffLite?.id,
                clia: cfg.clia,
                stateLicenseNumber: cfg.stateLicenseNumber,
                address: cfg.address,
            })),
        };
        try {
            return (await axios.post<LabVendor>(route, payload, { withCredentials: true })).data;
        } catch (error) {
            // TODO: need to consume the error and transform it into something consumable
            return thunk.rejectWithValue({ message: "failed to save vendor configuration" });
        }
    },
    {
        // Use of this action requires that the user is a SuperAdmin
        condition: (_, { getState }) => {
            const state = getState() as RootState;
            const isSuperAdmin = includesSuperAdminRoleGroup(state.user.user?.roleGroupIds);
            return isSuperAdmin;
        },
    },
);

const initialStateLabsVendorConfigDrawer: LabVendorConfigDrawerState = {
    show: false,
    onCloseDrawer: undefined,
    labVendor: undefined,
    saveStatus: "idle",
};
const labsVendorConfigDrawerSlice = createSlice({
    name: "labsVendorConfigDrawer",
    initialState: initialStateLabsVendorConfigDrawer,
    reducers: {},
    extraReducers: (builder) =>
        builder
            .addCase(openLabsVendorConfigDrawer, (state, action) => ({
                ...state,
                show: true,
                labVendor: action.payload.vendor,
                onCloseDrawer: action.payload.onCloseDrawer,
            }))
            .addCase(closeLabsVendorConfigDrawer, () => {
                return initialStateLabsVendorConfigDrawer;
            })
            .addCase(addLocationConfigurationToVendor, (state, action) => ({
                ...state,
                labVendor: {
                    ...state.labVendor,
                    locationConfigs: [...(state.labVendor?.locationConfigs ?? []), action.payload],
                } as LabVendor, // what's happening here is a little too fancy i guess
            }))
            .addCase(setLocationConfigurationForVendor, (state, action) => {
                const idx =
                    state.labVendor?.locationConfigs?.findIndex(
                        (cfg) => cfg.locationId === action.payload.locationId,
                    ) ?? -1;
                if (idx === -1) return state; // whatever the caller was trying to do is not gonna happen
                const splice_target = [...state.labVendor!.locationConfigs!];
                splice_target.splice(idx, 1, action.payload); // stitch in place
                return {
                    ...state,
                    labVendor: {
                        ...state.labVendor,
                        locationConfigs: [...splice_target],
                    } as LabVendor, // what's happening here is a little too fancy i guess
                };
            })
            .addCase(removeLocationConfigurationFromVendor, (state, action) => ({
                ...state,
                labVendor: {
                    ...state.labVendor,
                    locationConfigs: [
                        ...(state.labVendor?.locationConfigs?.filter(
                            (cfg) => cfg.locationId !== action.payload,
                        ) ?? []),
                    ],
                } as LabVendor, // what's happening here is a little too fancy i guess
            }))
            .addCase(saveLabsVendorConfigStaff.pending, (state, _) => ({
                ...state,
                saveStatus: "pending",
            }))
            .addCase(saveLabsVendorConfigStaff.fulfilled, (state, _) => ({
                ...state,
                saveStatus: "success",
            }))
            .addCase(saveLabsVendorConfigStaff.rejected, (state, _) => ({
                ...state,
                saveStatus: "failed",
            }))
            .addCase(saveLabsVendorConfigAdmin.pending, (state, _) => ({
                ...state,
                saveStatus: "pending",
            }))
            .addCase(saveLabsVendorConfigAdmin.fulfilled, (state, _) => ({
                ...state,
                saveStatus: "success",
            }))
            .addCase(saveLabsVendorConfigAdmin.rejected, (state, _) => ({
                ...state,
                saveStatus: "failed",
            })),
});

export const saveLabsVendorConfig = (state: RootState) => {
    return match(includesSuperAdminRoleGroup(state.user.user?.roleGroupIds))
        .with(true, () => saveLabsVendorConfigAdmin)
        .otherwise(() => saveLabsVendorConfigStaff);
};
export const isVendorConfigDrawerOpen = (state: RootState) => state.labsVendorConfigDrawer.show;
export const currentVendor = (state: RootState) => state.labsVendorConfigDrawer.labVendor;
export const onCloseVendorConfigDrawerCallback = (state: RootState) =>
    state.labsVendorConfigDrawer.onCloseDrawer;

export default labsVendorConfigDrawerSlice;
